import React, { Dispatch, SetStateAction, useState } from "react"

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@components/common/alert-dialog"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@components/common/select"
import { Tooltip, TooltipContent, TooltipTrigger } from "@components/common/tooltip"
import { Download, FileSpreadsheet, Upload } from "lucide-react"
import { Button } from "@components/common/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@components/common/card"
import { Input } from "@components/common/input"

import { Badge } from "@components/common/badge"
import { parseCookies } from "nookies"
import { ACCESS_TOKEN } from "@pma/utils/constants"
import axios from "axios"
import {
  BILL_CREATE__BY_UPLOAD_ENDPOINT,
  BILLS_BY_BOQ_ID_ENDPOINT,
  FINANCIAL_SERVER_URL,
  GET_MAIN_BOQ_ENDPOINT,
} from "@pma/utils/api-routes"
import { useMutation, useQuery } from "@tanstack/react-query"
import useTranslator from "@pma/hooks/use-translator"
import { Label } from "@components/common/label"
import { DictType } from "@pma/types"
import { exportToExcel, getFinancialErrorMessage } from "@pma/utils/helpers"
import { toast } from "@pma/hooks/use-toast"
import * as XLSX from "xlsx"
import { useNavigate, useSearchParams } from "react-router-dom"

interface Props {
  isOpen: boolean
  setIsOpen: Dispatch<SetStateAction<boolean>>
  boqId: string
}
const UploadBill = ({ isOpen, setIsOpen, boqId }: Props) => {
  const [templateId, setTemplateId] = useState("")
  const [uploadedFile, setUploadedFile] = useState<File | null>(null)

  const navigate = useNavigate()
  const [searchParam] = useSearchParams()
  const newParams = new URLSearchParams(searchParam)

  const { translate, locale } = useTranslator()

  const isHebrew = locale === "he"
  const accessToken = parseCookies()[ACCESS_TOKEN]

  const getAllBills = async () => {
    const response = await axios.post(
      FINANCIAL_SERVER_URL + BILLS_BY_BOQ_ID_ENDPOINT,
      {
        boq_id: boqId,
        type: "all",
      },
      {
        headers: {
          Authorization: "Bearer " + accessToken,
          "Content-Type": "application/json",
        },
      },
    )
    return response.data
  }

  const getMainBoQDetails = async () => {
    const response = await axios.post(
      FINANCIAL_SERVER_URL + GET_MAIN_BOQ_ENDPOINT,
      {
        boq_id: boqId,
      },
      {
        headers: {
          Authorization: "Bearer " + accessToken,
          "Content-Type": "application/json",
        },
      },
    )
    return response.data
  }

  const {
    data,
    isLoading: isLoadingAllBills,
    refetch: refetchAllBills,
  } = useQuery({
    queryKey: ["all-bills", boqId],
    queryFn: getAllBills,
  })

  const { data: boqFromTender, isLoading: isLoadingBoQ } = useQuery({
    queryKey: ["main-boq", boqId],
    queryFn: getMainBoQDetails,
  })

  const allBills = data?.items || []

  const resetForm = () => {
    setTemplateId("")
    setUploadedFile(null)
  }

  const handleCancel = () => {
    resetForm()
  }

  const exportBillToExcel = (data: DictType[]) => {
    const billKeys = isHebrew
      ? [
          "סכום boq",
          "סה'כ נוכחי מאושר",
          "סה'כ נוכחי ביקש",
          "הצעת החוק הנוכחית אושרה",
          "הצעת החוק הנוכחית המבוקשת",
          "מחיר ליחידה",
          "הנחה",
          "הערכה מחיר",
          "כמות מצטברת בפרויקט",
          "כמות מכתב הכמויות",
          "יחידה",
          "תיאור",
          "סעיף",
        ]
      : [
          "Section",
          "Description",
          "Unit",
          "Amount From estimate",
          "Accumulated Amount",
          "Estimate Price",
          "Discount (%)",
          "Price per unit",
          "Requested Current Bill",
          "Approved Current Bill",
          "Requested Current Total",
          "Approved Current Total",
          "Boq Sum",
        ]

    const sortedData = [...data].sort((a, b) =>
      a?._boq_item?.number?.localeCompare(b?._boq_item?.number),
    )

    const billExcelData = sortedData.map((item) => {
      const enValues = [
        item?._boq_item?.number,
        item?._boq_item?.description,
        item?._boq_item?.units,
        item?._boq_item?.amount,
        item?._boq_item?.paid || 0,
        item?._boq_item?.boq_cost || 0,
        `${(item?._boq_item?.boq_cost - item?._boq_item?.discount_price)?.toFixed(2)} (${item?._boq_item?.total_discount * 100})` ||
          0,
        `${item?._boq_item?.discount_price?.toFixed(2) || 0}`,
        item?._boq_item?.amount,
        item?._boq_item?.approved_amount,
        item?._boq_item?.discount_price * item?._boq_item?.amount,
        item?._boq_item?.discount_price * item?._boq_item?.approved_amount,
        item?._boq_item?.real_sum,
      ]

      const heValues = [
        item?._boq_item?.real_sum,
        item?._boq_item?.discount_price * item?._boq_item?.approved_amount,
        item?._boq_item?.discount_price * item?._boq_item?.amount,
        item?._boq_item?.approved_amount,
        item?._boq_item?.amount,
        `${item?._boq_item?.discount_price?.toFixed(2) || 0}`,
        `${(item?._boq_item?.boq_cost - item?._boq_item?.discount_price)?.toFixed(2)} (${item?._boq_item?.total_discount * 100})` ||
          0,
        item?._boq_item?.boq_cost || 0,
        item?._boq_item?.paid || 0,
        item?._boq_item?.amount,
        item?._boq_item?.units,
        item?._boq_item?.description,
        item?._boq_item?.number,
      ]

      return billKeys.reduce((acc: DictType, key, idx) => {
        acc[key] = isHebrew ? heValues[idx] : enValues[idx]
        return acc
      }, {})
    })

    const wb = XLSX.utils.book_new()
    const ws = XLSX.utils.json_to_sheet(billExcelData)

    ws["!cols"] = [
      { wch: 15 },
      { wch: isHebrew ? 10 : 40 },
      { wch: 10 },
      { wch: 10 },
      { wch: 15 },
      { wch: 20 },
      { wch: 15 },
      { wch: 10 },
      { wch: 10 },
      { wch: 20 },
      { wch: isHebrew ? 40 : 10 },
      { wch: 15 },
    ]
    ws["!dir"] = isHebrew ? "rtl" : "ltr"

    XLSX.utils.book_append_sheet(wb, ws, isHebrew ? "פריטי חשבונית" : "Bill Items")

    const firstBillItem = data[0]
    const billCreatedAt = firstBillItem?.created_at
      ? new Date(firstBillItem.created_at)
      : new Date()
    const dateStr = billCreatedAt.toISOString().split("T")[0]
    const filename = `bills-${data.length}-${isHebrew ? "פריטים" : "items"}-${dateStr}.xlsx`

    try {
      XLSX.writeFile(wb, filename, { bookType: "xlsx", type: "binary" })
    } catch (error) {
      const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" })
      const blob = new Blob([wbout], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    }
  }

  const handleDownloadTemplate = () => {
    let billItems = []

    if (allBills.length < 1) {
      billItems = boqFromTender?.boq_items?.map((item: DictType) => ({
        _boq_item: item,
      }))
    } else {
      const billToExport = allBills.filter((bill: DictType) => bill?.id === templateId)
      billItems = billToExport[0]?._bill_items
    }

    exportToExcel(billItems, "bills", {}, isHebrew)
    if (templateId) {
      resetForm()
    }
  }

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      const reader = new FileReader()
      reader.onload = (e) => {
        const bstr = e.target?.result

        const workbook = XLSX.read(bstr, { type: "binary" })
        const firstSheetName = workbook.SheetNames[0]
        const worksheet = workbook.Sheets[firstSheetName]
        const data = XLSX.utils.sheet_to_json(worksheet, { defval: "" })

        const cols = Object.keys(data[0] as object).filter((col: any) => col !== null && col !== "")

        const hasNumberColumn = cols.some(
          (column: any) => column === translate("Number") || column === "Number",
        )
        const hasAmountColumn = cols.some(
          (column: any) => column === translate("Current Bill") || column === "Current Bill",
        )

        if (hasNumberColumn && hasAmountColumn) {
          setUploadedFile(file)
        } else {
          resetForm()
          toast({
            title: translate("Info"),
            description: translate("Ensure the you are using the correct template"),
            variant: "info",
          })
        }
      }
      reader.readAsBinaryString(file)
    }
  }

  const uploadBill = async () => {
    const response = await axios.post(
      FINANCIAL_SERVER_URL + BILL_CREATE__BY_UPLOAD_ENDPOINT,
      {
        colunm_name_number: translate("Number"),
        column_name_amount: translate("Amount"),
        boq_id: boqId,
        xls_file: uploadedFile,
      },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "multipart/form-data",
        },
      },
    )

    return response.data
  }

  const uploadBillMutation = useMutation({
    mutationKey: ["upload-bill"],
    mutationFn: uploadBill,
    onSuccess: (data) => {
      newParams.set("revalidate", "bills")
      newParams.set("bill_tab", "all")
      navigate(`?${newParams.toString()}`)
      toast({
        title: translate("Success"),
        description: translate("bill uploaded successfully") + " — " + translate("moved to supervisor approval"),
        variant: "success",
      })

      refetchAllBills()
      resetForm()
      setIsOpen(false)
    },
    onError: (error: DictType) => {
      toast({
        title: translate("Error"),
        description: getFinancialErrorMessage(error, "upload bill", translate),
        variant: "error",
      })
    },
  })

  return (
    <AlertDialog open={isOpen} onOpenChange={setIsOpen}>
      <Tooltip>
        <TooltipTrigger>
          <AlertDialogTrigger>
            <Button
              variant={"outline"}
              leftSection={!isHebrew ? <Download size={14} /> : undefined}
              rightSection={isHebrew ? <Download size={14} /> : undefined}
              size={"sm"}
              disabled
            >
              {translate("Create From Template")}
            </Button>
          </AlertDialogTrigger>
        </TooltipTrigger>

        <TooltipContent>
          {translate("Download bill template to continue")}.{translate("Currently unavailable")}
        </TooltipContent>
      </Tooltip>
      <AlertDialogContent className='max-w-full lg:max-w-[50vw]'>
        <AlertDialogHeader>
          <AlertDialogTitle>{translate("Bill Template Options")}. </AlertDialogTitle>

          <div className='grid gap-4'>
            <div className='grid gap-4'>
              <div className='grid gap-2'>
                <Label htmlFor='template-type'>{translate("Template Type")}</Label>
                <Select value={templateId} onValueChange={setTemplateId}>
                  <SelectTrigger disabled={allBills?.length < 1}>
                    <SelectValue placeholder={translate("Select bill")} />
                  </SelectTrigger>
                  <SelectContent>
                    {allBills?.map((bill: DictType, index: number) => (
                      <SelectItem value={bill?.id} key={index}>
                        <div className='flex flex-col'>
                          <span>{bill?.number}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
                <Card className='cursor-pointer transition-colors hover:bg-muted/50'>
                  <CardHeader className='pb-2'>
                    <CardTitle className='flex items-center gap-2 text-sm'>
                      <Download className='h-4 w-4 text-blue-600' />
                      {translate("Download Template")}
                    </CardTitle>
                    <CardDescription className='text-xs'>
                      {translate("Download an Excel template pre-populated with bill data")}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className='pt-2'>
                    <Button
                      onClick={handleDownloadTemplate}
                      disabled={(allBills.length > 0 && !templateId) || !boqFromTender?.boq}
                      className='w-full'
                      size='sm'
                    >
                      <Download className='mr-1 h-4 w-4' />
                      {translate("Download Template")}
                    </Button>
                  </CardContent>
                </Card>

                <Card className='cursor-pointer transition-colors hover:bg-muted/50'>
                  <CardHeader className='pb-2'>
                    <CardTitle className='flex items-center gap-2 text-sm'>
                      <Upload className='h-4 w-4 text-green-600' />
                      {translate("Upload Completed Template")}
                    </CardTitle>
                    <CardDescription className='text-xs'>
                      {translate("Upload a completed template to create a new bill")}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className='pt-2'>
                    <div className='space-y-2'>
                      <label className='flex cursor-pointer items-center gap-2 rounded-md border border-input bg-background px-3 py-2 text-xs ring-offset-background hover:bg-accent hover:text-accent-foreground'>
                        <Input
                          type='file'
                          accept='.xlsx,.xls'
                          onChange={handleFileUpload}
                          className='hidden'
                        />
                        <Upload className='h-3 w-3' />
                        <span>{uploadedFile ? uploadedFile.name : translate("Choose File")}</span>
                      </label>
                      {uploadedFile && (
                        <div className='flex items-center gap-2 text-xs'>
                          <FileSpreadsheet className='h-3 w-3 text-green-600' />
                          <span className='truncate'>{uploadedFile.name}</span>
                          <Badge variant='outline' className='text-xs'>
                            {(uploadedFile.size / 1024 / 1024).toFixed(1)} MB
                          </Badge>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel
            onClick={() => {
              setIsOpen(false)
              handleCancel()
            }}
            className='w-[120px]'
          >
            {translate("Cancel")}
          </AlertDialogCancel>

          {uploadedFile && (
            <Button
              onClick={() => uploadBillMutation.mutate()}
              disabled={uploadBillMutation.isPending}
              className=''
            >
              <Upload className='mr-1 h-4 w-4' />
              {translate("Create Bill from Template")}
            </Button>
          )}

          {!uploadedFile && (
            <Button onClick={handleDownloadTemplate} disabled={!templateId}>
              <Download className='mr-1 h-4 w-4' />
              {translate("Download Template")}
            </Button>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

export default UploadBill
